/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 26/03/2025 - 13:21:19
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 26/03/2025
    * - Author          : 
    * - Modification    : 
**/
import React, { useState, useEffect } from "react";
import {
  Button,
  DropdownItem,
  DropdownMenu,
  Dropdown,
  DropdownToggle,
  Modal
} from "react-bootstrap";
import { Image } from "react-bootstrap";
import images from "../../images/index";
import { db } from "../../firebase";
import { useNavigate } from "react-router-dom";
import {
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { auth } from "../../firebase";
import { RxCaretDown } from "react-icons/rx";
import IconWrapper from "../../utils/IconWrapper";
import { useTranslation } from 'react-i18next';

interface TrashCardsProps {
  data?: any;
  onStatusChange?: any;
}

const TrashCards: React.FC<TrashCardsProps> = ({ data, onStatusChange }) => {
  const { t } = useTranslation(['trashCards', 'common']);
  const [reportList, setReportList] = useState<any[]>([]);
  const navigate = useNavigate();
  const [showRestoreActiveModal, setShowRestoreActiveModal] = useState(false);
  const [showRestoreArchiveModal, setShowRestoreArchiveModal] = useState(false);
  const [showDeleteForeverModal, setShowDeleteForeverModal] = useState(false);

  // Move project to the archive
  const moveToArchieve = async () => {
    setShowRestoreArchiveModal(false);
    const projectRef = doc(db, "projects", data.id);

    await updateDoc(projectRef, {
      status: "archieve",
    });
    onStatusChange();
  };
  
  // Move project to the active
  const moveToActive = async () => {
    setShowRestoreActiveModal(false);
    const projectRef = doc(db, "projects", data.id);

    await updateDoc(projectRef, {
      status: "active",
    });
    onStatusChange();
  };
  
  const deleteForever = async (id: any) => {
    setShowDeleteForeverModal(false);
    if (auth.currentUser?.uid) {
      await deleteDoc(doc(db, "projects", id));
      const q = query(
        collection(db, "paidReport"),
        where("userId", "==", auth.currentUser.uid),
        where("projectId", "==", id)
      );

      const querySnapshot = await getDocs(q);
      const deletePromises = querySnapshot.docs.map((document) =>
        deleteDoc(doc(db, "projects", document.id))
      );

      // Await all delete operations to finish
      await Promise.all(deletePromises).then(() => {
        onStatusChange();
      });
    }
  };

  useEffect(() => {
    const getReportList = async () => {
      if (auth.currentUser && data.id) {
        const q = query(
          collection(db, "paidReport"),
          where("userId", "==", auth.currentUser?.uid),
          where("projectId", "==", data.id)
        );
        const querySnapshot = await getDocs(q);
        const newData = querySnapshot.docs.map((doc) => {
          return { ...doc.data(), id: doc.id };
        });
        setReportList(newData);
      }
    };

    getReportList();
  }, [data.id]);

  return (
    <React.Fragment>
      {data.id && (
        <div className="bg-light activeCard p-2">
          <div className="d-flex justify-content-between align-items-center">
            <p className="projectName">{data.projectName}</p>
            <Dropdown>
              <Dropdown.Toggle
                className="activeDropdown p-0 mb-2"
                variant="none"
              >
                <Image
                  className="mb-1"
                  src={images.SettingIcon}
                  alt="setting_option"
                />
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() => setShowRestoreActiveModal(true)}
                  className="d-flex align-items-center activeDropDownItem"
                >
                  <Image width={20} src={images.ActiveIcon} />
                  <span className="mx-2">{t('trashCards:buttons.restoreActive')}</span>
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => setShowRestoreArchiveModal(true)}
                  className="d-flex align-items-center activeDropDownItem"
                >
                  <Image width={20} src={images.ArchieveIcon} />
                  <span className="mx-2">{t('trashCards:buttons.restoreArchive')}</span>
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => setShowDeleteForeverModal(true)}
                  className="d-flex align-items-center activeDropDownItem"
                >
                  <Image width={20} src={images.TrashIcon} />
                  <span className="mx-2">{t('trashCards:buttons.deleteForever')}</span>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <Image
            src={data.projectPhoto}
            style={{ width: "100%", height: "156px", objectFit: "contain" }}
            alt="projectImg"
          />
          <div className="d-block d-md-flex justify-content-between align-items-center mt-2">
            <Dropdown>
              <DropdownToggle variant="none" className="m-0 p-0 border-0">
                <Button className="mt-2 activeCalculated d-flex justify-content-center align-items-center">
                  {reportList.length > 0 ? (
                    <div className="d-flex justify-content-center align-items-center gap-2">
                      <img
                        src={images.Checkedicon}
                        width={20}
                        height={20}
                        style={{ objectFit: "contain" }}
                      />
                      <span> {t('common:results')} </span>
                      <IconWrapper 
                        Icon={RxCaretDown} 
                        style={{ marginTop: "1px" }}
                        size={25}
                        color="#000"
                      />
                    </div>
                  ) : (
                    <div className="d-flex justify-content-center align-items-center gap-2">
                      <img
                        src={images.CrossIcon}
                        width={20}
                        height={20}
                        style={{ objectFit: "contain" }}
                      />
                      <span>{t('common:noResults')} </span>
                    </div>
                  )}
                </Button>
              </DropdownToggle>
              {reportList.length > 0 && (
                <DropdownMenu className="reportListDropdownMenu p-3 mt-1">
                  {reportList.map((value) => {
                    return (
                      <DropdownItem
                        onClick={() => {
                          if (value.paymentStatus) {
                            navigate("/paidproject", {
                              state: value.id,
                            });
                          } else {
                            navigate("/unpaidproject", {
                              state: { projectId: value.id },
                            });
                          }
                        }}
                        key={value.id}
                        className="py-3 px-2 mx-0 mb-3 reportListDropdownList"
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <p className="m-0 p-0"> {t('common:id')}: {value.id} </p>
                          <p style={{ color: "#0984e3" }} className="m-0 p-0">
                            {" "}
                            {value.paymentStatus ? t('common:paid') : t('common:free')}{" "}
                          </p>
                        </div>
                        <hr style={{ border: "1px solid #000" }} />
                        <p> {t('common:createdAt')} {value.date}</p>
                        <p>
                          {" "}
                          {value.verticalPVArea + value.tiltedPVArea} {t('common:pvAreas')}
                        </p>
                        <p> {value.detectionPoints} {t('common:detectionPoints')}</p>
                      </DropdownItem>
                    );
                  })}
                </DropdownMenu>
              )}
            </Dropdown>
            <Button
              className="mt-2 activeOPenProjects px-2 py-0"
              onClick={() => {
                navigate(`/map_page`, { state: data.id });
              }}
            >
              {t('trashCards:buttons.view')}
            </Button>
          </div>
        </div>
      )}

      {/* Restore to Active Modal */}
      <Modal show={showRestoreActiveModal} onHide={() => setShowRestoreActiveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('trashCards:modals.restoreActive.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('trashCards:modals.restoreActive.message')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRestoreActiveModal(false)}>
            {t('trashCards:modals.restoreActive.cancel')}
          </Button>
          <Button variant="primary" onClick={moveToActive}>
            {t('trashCards:modals.restoreActive.confirm')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Restore to Archive Modal */}
      <Modal show={showRestoreArchiveModal} onHide={() => setShowRestoreArchiveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('trashCards:modals.restoreArchive.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('trashCards:modals.restoreArchive.message')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRestoreArchiveModal(false)}>
            {t('trashCards:modals.restoreArchive.cancel')}
          </Button>
          <Button variant="primary" onClick={moveToArchieve}>
            {t('trashCards:modals.restoreArchive.confirm')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Forever Modal */}
      <Modal show={showDeleteForeverModal} onHide={() => setShowDeleteForeverModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('trashCards:modals.deleteForever.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('trashCards:modals.deleteForever.message')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteForeverModal(false)}>
            {t('trashCards:modals.deleteForever.cancel')}
          </Button>
          <Button variant="danger" onClick={() => deleteForever(data.id)}>
            {t('trashCards:modals.deleteForever.confirm')}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default TrashCards;